import * as React from 'react';
import { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { renderEditorView } from './EditorView';
import { cropImage } from '../utils/cropImage';
import { ZOOM_SENSITIVITY_WHEEL, BASE_ICON_SIZE, BASE_NODE_RADIUS, BASE_PIPE_WIDTH, HIT_RADIUS_PX, DRAG_THRESHOLD, CLICK_GUARD_MS, OBJECT_SNAP_TOLERANCE_PX, INITIAL_VIEW_PADDING, availableFixtures, availableFittings, availableDirections } from './Editor.constants';
import { ToolType, SymbolType, SystemType, MarkupState, Point, Line, ViewSettings, TradeSymbol } from '@shared/types/tsk';
import { useCanvasCoordinates } from '../hooks/useCanvasCoordinates';
import { useEditorGestures } from '../hooks/useEditorGestures';
import { getPlanReviewChecks } from '../utils/compliance';
import { SYSTEM_COLORS } from '../utils/drawing';
interface EditorProps {
  image: string;
  setImage?: (img: string) => void;
  markup: MarkupState;
  setMarkup: (markup: MarkupState | ((prev: MarkupState) => MarkupState), pushToHistory?: boolean) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  activeTool: ToolType;
  setActiveTool: (tool: ToolType) => void;
  activeSymbol: SymbolType;
  setActiveSymbol: (symbol: SymbolType) => void;
  activeSystem: SystemType;
  setActiveSystem: (system: SystemType) => void;
  viewSettings: ViewSettings;
  setViewSettings: (settings: ViewSettings) => void;
}
interface CropRect {
  startWorld: Point;
  endWorld: Point;
}
interface Selection {
  id: string;
  type: 'symbol' | 'node';
}
const ZOOM_SENSITIVITY_WHEEL = 0.001;
const BASE_ICON_SIZE = 32;
const BASE_NODE_RADIUS = 3;
const BASE_PIPE_WIDTH = 2;
const HIT_RADIUS_PX = 48;
const DRAG_THRESHOLD = 8;
const CLICK_GUARD_MS = 300;
const OBJECT_SNAP_TOLERANCE_PX = 14;
const INITIAL_VIEW_PADDING = 40;
const Editor = ({
  image, setImage, markup, setMarkup, undo, redo, canUndo, canRedo,
  activeTool, setActiveTool, activeSymbol, setActiveSymbol,
  activeSystem, setActiveSystem,
  viewSettings, setViewSettings
}: EditorProps) => { const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const markupRef = useRef<MarkupState>(markup);
  const {
    scale, setScale, offset, setOffset, setViewport, worldSize, setWorldSize, viewportRef,
    getLocalCoords, getCoords, applyZoom, snapPoint
  } = useCanvasCoordinates(containerRef, viewSettings, markupRef);
  const [isViewSettingsOpen, setIsViewSettingsOpen] = useState(false);
  const [isGridSettingsOpen, setIsGridSettingsOpen] = useState(false);
  useEffect(() => { markupRef.current = markup; }, [markup]);
  useEffect(() => { viewportRef.current = { scale, offset }; }, [scale, offset]);
  useEffect(() => { const calibrate = () => { const img = imgRef.current;
      const container = containerRef.current;
      if (img) {
      }
      if (!img || !container || img.naturalWidth < 1) {
        return;
      }
      const nw = img.naturalWidth, nh = img.naturalHeight;
      const containerW = container.clientWidth - (INITIAL_VIEW_PADDING * 2);
      const containerH = container.clientHeight - (INITIAL_VIEW_PADDING * 2);
      setWorldSize({ w: nw, h: nh });
      if (viewSettings.gridSize === 20) { 
         const autoGridSize = Math.max(10, Math.round(nw / 50));
         setViewSettings({ ...viewSettings, gridSize: autoGridSize });
      }
      const s = Math.min(containerW / nw, containerH / nh);
      setViewport({
        scale: s,
        offset: { x: (container.clientWidth - nw * s) / 2, y: (container.clientHeight - nh * s) / 2 }
      });
    };
    const img = imgRef.current;
    if (img) {
      if (img.complete && img.naturalWidth >= 1) {
        calibrate();
      } else {
        img.onload = calibrate;
      }
    }
  }, [image]); 
  useEffect(() => { setSelection(null);
    setDraftPoints(null);
    setDraggingSymbolId(null);
    setActiveGrip(null);
    setIsPanning(false);
  }, [activeTool]);
  const selectionTimeRef = useRef<number>(0);
  const [selection, setSelection] = useState<Selection | null>(null);
  const [draftPoints, setDraftPoints] = useState<Point[] | null>(null);
  
  const updateSelection = (sel: Selection | null) => { 
    setSelection(sel);
    if (sel) selectionTimeRef.current = Date.now();
  };

  const {
    handlePointerDown, handlePointerMove, handlePointerUp, resetGestures,
    isPanning, cursorPos, setCursorPos, isSnappedToSymbol, setIsSnappedToSymbol,
    activeGrip, setActiveGrip: useStateActiveGrip, draggingSymbolId
  } = useEditorGestures({
    getCoords, getLocalCoords, snapPoint, viewportRef, activeTool, activeSymbol,
    activeSystem, markupRef, draftPoints, selection, updateSelection, 
    setMarkup, setDraftPoints, setScale, setOffset
  });
  const [cropRect, setCropRect] = useState<CropRect | null>(null);
  const [isCropDragging, setIsCropDragging] = useState(false);
  const [isCropping, setIsCropping] = useState(false);
  const liveSelectionData = useMemo(() => { if (!selection) return null;
    if (selection.type === 'symbol') {
      const sym = markup.symbols.find(s => s.id === selection.id);
      return sym ? { ...sym, isSymbol: true } : null;
    } else {
      const [lineId, nodeIdx] = selection.id.split('-');
      const line = markup.lines.find(l => l.id === lineId);
      const point = line?.points[parseInt(nodeIdx)];
      return point ? { position: point, system: line!.system, isNode: true, lineId } : null;
    }
  }, [markup, selection]);
  const compliance = useMemo(() => getPlanReviewChecks(markup), [markup]);
  useEffect(() => { (window as any).__TSK_VIEWPORT__ = { scale, offset };
  }, [scale, offset]);
  const isClickGuarded = () => (Date.now() - selectionTimeRef.current) < CLICK_GUARD_MS;
  const handleUndo = useCallback(() => { if (draftPoints && draftPoints.length > 0) {
      setDraftPoints(prev => {
        if (!prev || prev.length <= 1) {
          return null;
        }
        const next = prev.slice(0, -1);
        return next;
      });
    } else {
      undo();
    }
  }, [draftPoints, undo]);
  useEffect(() => { const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') {
        if (draftPoints && draftPoints.length > 0) {
          handleUndo();
        } else {
          updateSelection(null);
        }
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        handleUndo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, draftPoints]);
  const setActiveGrip = (val: { lineId: string, pointIndex: number } | null) => { useStateActiveGrip(val);
  };
  const activeGripRef = useRef<{ lineId: string, pointIndex: number } | null>(null);
  useEffect(() => { activeGripRef.current = activeGrip; }, [activeGrip]);
  const handleCropPointerDown = (e: React.PointerEvent) => { const { world } = getCoords(e.clientX, e.clientY);
    setCropRect({ startWorld: world, endWorld: world });
    setIsCropDragging(true);
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };
  const handleCropPointerMove = (e: React.PointerEvent) => { if (!isCropDragging) return;
    const { world } = getCoords(e.clientX, e.clientY);
    setCropRect(prev => prev ? { ...prev, endWorld: world } : null);
  };
  const handleCropPointerUp = () => { setIsCropDragging(false);
  };
  const confirmCrop = async () => { if (!cropRect || !setImage) return;
    setIsCropping(true);
    const x = Math.min(cropRect.startWorld.x, cropRect.endWorld.x);
    const y = Math.min(cropRect.startWorld.y, cropRect.endWorld.y);
    const w = Math.abs(cropRect.endWorld.x - cropRect.startWorld.x);
    const h = Math.abs(cropRect.endWorld.y - cropRect.startWorld.y);
    if (w > 10 && h > 10) {
      try {
        const cropped = await cropImage(image, { x, y, w, h });
        setImage(cropped);
      } catch (err) {
        console.error('Crop failed:', err);
      }
    }
    setCropRect(null);
    setIsCropping(false);
    setActiveTool(ToolType.PAN);
  };
  const cancelCrop = () => { setCropRect(null);
    setActiveTool(ToolType.PAN);
  };
  // Pointer handlers delegated to useEditorGestures hook
  const stopBubbling = (e: React.PointerEvent | React.MouseEvent) => { e.stopPropagation(); };
  const finishActivePipe = (e: React.MouseEvent) => { e.stopPropagation();
    if (!draftPoints || draftPoints.length < 2) {
      setDraftPoints(null);
      return;
    }
    setMarkup((prev: MarkupState) => ({
      ...prev,
      lines: [...prev.lines, { id: Date.now().toString(), points: [...draftPoints], color: SYSTEM_COLORS[activeSystem as SystemType], system: activeSystem }]
    }));
    setDraftPoints(null);
  };
  const deleteSelected = () => { if (isClickGuarded() || !selection) return;
    setMarkup((prev: MarkupState) => ({
      ...prev,
      symbols: prev.symbols.filter((s: TradeSymbol) => s.id !== selection.id),
      lines: prev.lines.map((l: Line) => ({
        ...l,
        points: l.points.map((p: Point) => p.attachedSymbolId === selection.id ? { ...p, attachedSymbolId: undefined } : p)
      })).filter((l: Line) => l.id !== selection.id.split('-')[0])
    }));
    updateSelection(null);
  };
  const changeSelectedSystem = (newSystem: SystemType) => { if (isClickGuarded() || !selection || !liveSelectionData || liveSelectionData.system === newSystem) return;
    setMarkup((prev: MarkupState) => ({
      ...prev,
      symbols: prev.symbols.map((s: TradeSymbol) => s.id === selection.id ? { ...s, system: newSystem } : s),
      lines: prev.lines.map((l: Line) => l.id === (liveSelectionData as any).lineId || l.id === selection.id.split('-')[0] ? { ...l, system: newSystem, color: SYSTEM_COLORS[newSystem] } : l)
    }));
  };
  const changeSelectedSymbolType = (newType: SymbolType) => { if (isClickGuarded() || !selection || selection.type !== 'symbol' || (liveSelectionData as any).type === newType) return;
    setMarkup((prev: MarkupState) => ({
      ...prev,
      symbols: prev.symbols.map((s: TradeSymbol) => s.id === selection.id ? { ...s, type: newType } : s)
    }));
  };
  const currentIconSize = BASE_ICON_SIZE * viewSettings.symbolScale;
  const currentNodeRadius = BASE_NODE_RADIUS * viewSettings.symbolScale;
  const currentPipeWidth = BASE_PIPE_WIDTH * viewSettings.symbolScale;
  const floatingPos = liveSelectionData ? {
    x: liveSelectionData.position.x * scale + offset.x,
    y: liveSelectionData.position.y * scale + offset.y
  } : null;
  return renderEditorView({ containerRef, isPanning, handlePointerDown, handlePointerMove, handlePointerUp, setCursorPos, setIsSnappedToSymbol, handleWheel: (e: any) => { const focal = getLocalCoords(e.clientX, e.clientY); applyZoom(scale * (1 - e.deltaY * ZOOM_SENSITIVITY_WHEEL), focal); }, offset, scale, imgRef, image, worldSize, viewSettings, markup, activeTool, activeSystem, selection, activeGrip, draggingGripId: null, lineBuilding: false, cursorPos, isSnappedToSymbol, currentPipeWidth, isCropMode: false, cropRect, handleCropPointerDown, handleCropPointerMove, handleCropPointerUp, cancelCrop, confirmCrop, floatingPos, liveSelectionData, changeSelectedSystem, changeSelectedSymbolType, deleteSelected, availableFixtures, availableFittings, availableDirections, currentIconSize, finishActivePipe, stopBubbling, setActiveTool, setActiveSymbol, setActiveSystem, undo, canUndo, redo, canRedo, setIsViewSettingsOpen, setIsGridSettingsOpen, isViewSettingsOpen, isGridSettingsOpen, compliance, setViewSettings });};
export default Editor;
